import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { StandardCommunicationChannel } from '@/constants/channels'

interface AssignedSessionsFiltersState {
  channelFilters: StandardCommunicationChannel[]
  agentFilters: number[]
  contactFilters: string[]
  includeUnassigned: boolean
  dateFrom: string
  dateTo: string
  contactSearch: string
  areFiltersCollapsed: boolean
  setChannelFilters: (channels: StandardCommunicationChannel[]) => void
  toggleChannelFilter: (slug: StandardCommunicationChannel) => void
  setAgentFilters: (agentIds: number[]) => void
  toggleAgentFilter: (agentId: number) => void
  setContactFilters: (contactIds: string[]) => void
  toggleContactFilter: (contactId: string) => void
  setIncludeUnassigned: (include: boolean) => void
  setDateFrom: (value: string) => void
  setDateTo: (value: string) => void
  setContactSearch: (value: string) => void
  setFiltersCollapsed: (collapsed: boolean) => void
  resetFilters: () => void
}

const DEFAULT_FILTER_STATE: Pick<
  AssignedSessionsFiltersState,
  | 'channelFilters'
  | 'agentFilters'
  | 'contactFilters'
  | 'includeUnassigned'
  | 'dateFrom'
  | 'dateTo'
  | 'contactSearch'
> = {
  channelFilters: [],
  agentFilters: [],
  contactFilters: [],
  includeUnassigned: true,
  dateFrom: '',
  dateTo: '',
  contactSearch: '',
}

export const useAssignedSessionsFiltersStore =
  create<AssignedSessionsFiltersState>()(
    persist(
      (set, get) => ({
        channelFilters: DEFAULT_FILTER_STATE.channelFilters,
        agentFilters: DEFAULT_FILTER_STATE.agentFilters,
        contactFilters: DEFAULT_FILTER_STATE.contactFilters,
        includeUnassigned: DEFAULT_FILTER_STATE.includeUnassigned,
        dateFrom: DEFAULT_FILTER_STATE.dateFrom,
        dateTo: DEFAULT_FILTER_STATE.dateTo,
        contactSearch: DEFAULT_FILTER_STATE.contactSearch,
        areFiltersCollapsed: true,
        setChannelFilters: channels => {
          set({
            channelFilters: Array.from(
              new Set<StandardCommunicationChannel>(channels)
            ),
          })
        },
        toggleChannelFilter: slug => {
          set(state => {
            const exists = state.channelFilters.includes(slug)
            return {
              channelFilters: exists
                ? state.channelFilters.filter(value => value !== slug)
                : [...state.channelFilters, slug],
            }
          })
        },
        setAgentFilters: agentIds => {
          set({
            agentFilters: Array.from(
              new Set(agentIds.filter(id => Number.isFinite(id)))
            ),
          })
        },
        toggleAgentFilter: agentId => {
          set(state => {
            if (!Number.isFinite(agentId)) {
              return state
            }
            const exists = state.agentFilters.includes(agentId)
            return {
              agentFilters: exists
                ? state.agentFilters.filter(id => id !== agentId)
                : [...state.agentFilters, agentId],
            }
          })
        },
        setContactFilters: contactIds => {
          set({
            contactFilters: Array.from(new Set(contactIds)),
          })
        },
        toggleContactFilter: contactId => {
          set(state => {
            const exists = state.contactFilters.includes(contactId)
            const newFilters = exists
              ? state.contactFilters.filter(id => id !== contactId)
              : [...state.contactFilters, contactId]
            console.log('🔍 [DEBUG] Store contact filters updated:', {
              contactId,
              wasExisting: exists,
              oldFilters: state.contactFilters,
              newFilters,
            })
            return {
              contactFilters: newFilters,
            }
          })
        },
        setIncludeUnassigned: include => {
          set({ includeUnassigned: include })
        },
        setDateFrom: value => {
          set({ dateFrom: value })
        },
        setDateTo: value => {
          set({ dateTo: value })
        },
        setContactSearch: value => {
          set({ contactSearch: value })
        },
        setFiltersCollapsed: collapsed => {
          set({ areFiltersCollapsed: collapsed })
        },
        resetFilters: () => {
          const current = get()
          set({
            channelFilters: DEFAULT_FILTER_STATE.channelFilters,
            agentFilters: DEFAULT_FILTER_STATE.agentFilters,
            contactFilters: DEFAULT_FILTER_STATE.contactFilters,
            includeUnassigned: DEFAULT_FILTER_STATE.includeUnassigned,
            dateFrom: DEFAULT_FILTER_STATE.dateFrom,
            dateTo: DEFAULT_FILTER_STATE.dateTo,
            contactSearch: DEFAULT_FILTER_STATE.contactSearch,
            areFiltersCollapsed: current.areFiltersCollapsed,
          })
        },
      }),
      {
        name: 'assigned-sessions-filters',
        partialize: state => ({
          channelFilters: state.channelFilters,
          agentFilters: state.agentFilters,
          contactFilters: state.contactFilters,
          includeUnassigned: state.includeUnassigned,
          dateFrom: state.dateFrom,
          dateTo: state.dateTo,
          contactSearch: state.contactSearch,
          areFiltersCollapsed: state.areFiltersCollapsed,
        }),
      }
    )
  )
